import { StandardEvents } from '@shopify/events';

// Keep existing Custom Liquid notices working until they are replaced with the block.
const DEFAULT_LOW_STOCK_VALUES = 'Black, Nude';
const PRODUCT_SCOPE =
  'product-card, dialog, quick-add-dialog, product-information, featured-product-information, .shopify-section, [id^="shopify-section-"]';
const DATE_ATTRIBUTES = ['data-days-ahead', 'data-skip-sunday', 'data-skip-saturday', 'data-locale'];
const normalizeValue = (value) => value.normalize('NFKC').trim().replace(/\s+/gu, ' ').toLowerCase();

class ShippingNotice extends HTMLElement {
  static observedAttributes = [
    ...DATE_ATTRIBUTES,
    'data-product-id',
    'data-selected-options',
    'data-option-names',
    'data-low-stock-values',
    'data-low-stock-text',
    'data-in-stock-text',
    'data-show-matching-values',
    'data-matching-values-prefix',
  ];

  #abortController = new AbortController();
  #variantRevision = 0;

  connectedCallback() {
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }

    const { signal } = this.#abortController;
    const scope = this.closest(PRODUCT_SCOPE) || document;

    window.addEventListener('pageshow', this.#handlePageShow, { signal });
    document.addEventListener('visibilitychange', this.#handleVisibilityChange, { signal });

    // A notice can sit inside the bundle or in a sibling block in the same product section.
    // Delegate to that section so replacing a bundle in the editor does not leave stale listeners.
    scope.addEventListener('bundle:state-change', this.#handleBundleStateChange, { signal });
    scope.addEventListener('change', this.#handleVariantChange, { signal, capture: true });
    scope.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });

    // A notice inserted by the editor may connect before its children are parsed.
    queueMicrotask(() => {
      if (signal.aborted || !this.isConnected) return;
      this.#renderDate();
      this.#renderStockState();
    });
  }

  disconnectedCallback() {
    this.#abortController.abort();
    this.#variantRevision += 1;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (!this.isConnected || oldValue === newValue) return;
    if (DATE_ATTRIBUTES.includes(name)) this.#renderDate();
    else this.#renderStockState();
  }

  #handlePageShow = () => {
    this.#renderDate();
    this.#renderStockState();
  };

  #handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') this.#renderDate();
  };

  #handleBundleStateChange = (event) => {
    if (event.target !== this.#getBundle()) return;
    this.#variantRevision += 1;
    // Read only after the bundle has reconciled options, swatches and all paid/free units.
    this.#renderStockState();
  };

  #isOwnPickerEvent(event) {
    if (this.#getBundle() || !(event.target instanceof HTMLElement)) return false;
    const picker = event.target.closest('variant-picker');
    return picker !== null && picker === this.#getVariantPicker();
  }

  #handleVariantChange = (event) => {
    if (!this.#isOwnPickerEvent(event)) return;
    this.#variantRevision += 1;
    this.#renderStockState();
  };

  #handleProductSelect = async (event) => {
    if (!this.#isOwnPickerEvent(event)) return;
    const revision = ++this.#variantRevision;
    this.#renderStockState();

    try {
      const result = await event.promise;
      if (!this.isConnected || revision !== this.#variantRevision || this.#getBundle()) return;

      // Horizon can replace the picker, or switch products in a combined listing.
      const { detail } = result || {};
      if (detail?.newProduct?.id) this.dataset.productId = String(detail.newProduct.id);
      if (Array.isArray(detail?.resource?.options)) {
        this.dataset.selectedOptions = JSON.stringify(detail.resource.options);
      }
      this.#renderStockState();
    } catch {
      // An aborted or failed variant request must not overwrite the current selection.
    }
  };

  #getBundle() {
    const parentBundle = this.closest('bundle-quantity');
    if (parentBundle) return parentBundle;

    const scope = this.closest(PRODUCT_SCOPE);
    if (!scope) return null;

    const productId = this.dataset.productId;
    const bundles = [...scope.querySelectorAll('bundle-quantity')].filter((bundle) =>
      bundle.closest(PRODUCT_SCOPE) === scope &&
      !bundle.closest('[data-bundle-upsell]') &&
      (!productId || bundle.dataset.productId === productId)
    );

    // Prefer the same details panel; never borrow selections from another product/modal.
    const details = this.closest('.product-details');
    if (details) {
      const localBundles = bundles.filter((bundle) => bundle.closest('.product-details') === details);
      if (localBundles.length === 1) return localBundles[0];
      if (localBundles.length > 1) return null;
    }
    return bundles.length === 1 ? bundles[0] : null;
  }

  #getVariantPicker() {
    const productId = this.dataset.productId;
    const scope = this.closest(PRODUCT_SCOPE);

    if (scope) {
      const pickers = [...scope.querySelectorAll('variant-picker')].filter(
        (picker) => picker.closest(PRODUCT_SCOPE) === scope
      );
      if (productId) return pickers.find((picker) => picker.dataset.productId === productId) || null;
      return pickers.length === 1 ? pickers[0] : null;
    }

    // Legacy Custom Liquid outside a product section may use the main PDP picker.
    return [...document.querySelectorAll('variant-picker[data-template-product-match="true"]')].find(
      (picker) =>
        !picker.closest('product-card, dialog, quick-add-dialog') &&
        (!productId || picker.dataset.productId === productId)
    ) || null;
  }

  #readArray(value) {
    try {
      const parsed = JSON.parse(value || '[]');
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** @param {Element} bundle */
  #getBundleOptions(bundle) {
    const radio = [...bundle.querySelectorAll('[data-bundle-tier-radio]:checked')].find(
      (control) => control.closest('bundle-quantity') === bundle
    );
    const tier = radio?.closest('[data-bundle-tier]');
    if (!tier) return [];

    const names = this.#readArray(this.dataset.optionNames);
    const values = [];
    // Include every paid/free unit in the active tier, but never other tiers or upsell products.
    // Native selects are canonical; the bundle has already mirrored any swatch changes into them.
    for (const control of tier.querySelectorAll('[data-bundle-option-select]')) {
      if (!(control instanceof HTMLSelectElement) || control.closest('bundle-quantity') !== bundle ||
          control.closest('[data-bundle-tier]') !== tier || control.closest('[data-bundle-upsell]')) continue;
      const name = control.closest('[data-bundle-option]')?.getAttribute('data-option-name') ||
        names[Number(control.dataset.optionPosition) - 1] || '';
      if (control.value) values.push({ name, value: control.value.trim() });
    }
    return values;
  }

  #getSelectedOptions() {
    const bundle = this.#getBundle();
    if (bundle) {
      const values = this.#getBundleOptions(bundle);
      if (values.length) return values;
    }

    // A bundle without selectors uses its Liquid variant, never a separate PDP/modal picker.
    const picker = bundle ? null : this.#getVariantPicker();
    if (picker) {
      const values = [];
      for (const control of picker.querySelectorAll('input[type="radio"]:checked, select')) {
        const option = control instanceof HTMLSelectElement ? control.selectedOptions[0] : control;
        const name = option?.dataset.optionName || control.dataset.optionName ||
          control.name?.match(/^options\[(.+)]$/)?.[1] || '';
        if (control.value) values.push({ name, value: control.value.trim() });
      }
      if (values.length) return values;
    }

    // Products with no picker still have the selected variant supplied by Liquid.
    const names = this.#readArray(this.dataset.optionNames);
    return this.#readArray(this.dataset.selectedOptions)
      .filter((value) => typeof value === 'string')
      .map((value, index) => ({ name: names[index] || '', value: value.trim() }));
  }

  #renderStockState = () => {
    const output = this.querySelector('[data-shipping-notice-stock]') ||
      this.querySelector('.shipping-notice__text strong');
    if (!(output instanceof HTMLElement)) return;

    // An explicitly empty list disables low-stock matching; missing attributes retain legacy defaults.
    const triggers = new Set(
      (this.dataset.lowStockValues ?? DEFAULT_LOW_STOCK_VALUES)
        .split(',').map(normalizeValue).filter(Boolean)
    );
    const seen = new Set();
    const matches = this.#getSelectedOptions().filter(({ value }) => {
      const normalized = normalizeValue(value);
      if (!triggers.has(normalized) || seen.has(normalized)) return false;
      seen.add(normalized);
      return true;
    });
    const isLowStock = matches.length > 0;
    const stockText = isLowStock
      ? (this.dataset.lowStockText ?? 'Low stock').trim()
      : (this.dataset.inStockText ?? 'In stock').trim();
    const matchingValues = matches.map(({ value }) => value).join(', ');
    const showValues = isLowStock && stockText && this.dataset.showMatchingValues !== 'false';
    const prefix = (this.dataset.matchingValuesPrefix ?? 'in').trim();

    output.textContent = showValues ? [stockText, prefix, matchingValues].filter(Boolean).join(' ') : stockText;
    output.hidden = !stockText;
    this.dataset.stockState = isLowStock ? 'low' : 'in-stock';
    this.style.setProperty('--shipping-notice-dot-color', isLowStock
      ? 'var(--shipping-notice-low-stock-dot-color, #D97706)'
      : 'var(--color-instock)');

    if (isLowStock) this.dataset.stockValues = matchingValues;
    else this.removeAttribute('data-stock-values');

    const colour = matches.find(({ name }) => /^(colour|color|cor)$/i.test(name));
    if (colour) this.dataset.stockColour = colour.value;
    else this.removeAttribute('data-stock-colour');
  };

  #renderDate = () => {
    const output = this.querySelector('[data-shipping-notice-date]');
    if (!(output instanceof HTMLTimeElement)) return;

    const configuredDays = Number(this.dataset.daysAhead ?? 1);
    const daysAhead = Number.isFinite(configuredDays) ? Math.max(0, Math.min(14, Math.trunc(configuredDays))) : 1;
    const skipSunday = this.dataset.skipSunday !== 'false';
    const skipSaturday = this.dataset.skipSaturday === 'true';
    const locale = this.dataset.locale?.trim() || 'en-GB';
    const date = new Date();

    // Calendar arithmetic at local noon avoids UTC date shifts and midnight DST transitions.
    date.setHours(12, 0, 0, 0);
    date.setDate(date.getDate() + daysAhead);
    while ((skipSunday && date.getDay() === 0) || (skipSaturday && date.getDay() === 6)) {
      date.setDate(date.getDate() + 1);
    }

    const format = { weekday: 'short', month: 'short', day: 'numeric' };
    try {
      const supportedLocale = Intl.DateTimeFormat.supportedLocalesOf([locale])[0] || 'en-GB';
      output.textContent = new Intl.DateTimeFormat(supportedLocale, format).format(date);
    } catch {
      output.textContent = new Intl.DateTimeFormat('en-GB', format).format(date);
    }

    output.dateTime = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };
}

if (!customElements.get('shipping-notice')) {
  customElements.define('shipping-notice', ShippingNotice);
}